import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FireOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  SearchOutlined,
  CalendarOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  LeftOutlined,
  RightOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  BarChartOutlined,
  ExpandAltOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";
import axios from "../../utils/axiosDash";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const STORE_STORAGE_KEY = "selectedStore";
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_STATS = { total: 0, active: 0, avgSeconds: null, longestSeconds: null, storeCount: 0 };
const EMPTY_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 };

// ---------------------------------------------------------------------------
// FIELD MAP — first match wins.
// Fields such as postgresId, channel, cameraIp, createdAt are intentionally
// NOT mapped and are hidden from the UI (including the "More details" box).
// ---------------------------------------------------------------------------
const FIELDS = {
  id: ["_id", "id"],
  store: ["restaurant", "store"],
  start: ["startTime"],
  end: ["endTime", "end_time", "endedAt"],
  durationSec: ["durationSeconds", "duration", "durationInSeconds"],
  maxAbsenceSec: ["maxAbsenceSeconds"],
  count: ["kebabCount", "count", "quantity", "pieces", "skewers"],
  status: ["status"],
  startImage: ["startImagePath", "startImage", "start_image"],
  endImage: ["endImagePath", "endImage", "end_image"],
};

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== "") return obj[k];
  }
  return null;
};

// ---------------------------------------------------------------------------
// Time handling
// The stored timestamps already hold the correct wall-clock time, so we show
// them exactly as stored: parse as UTC and format in UTC. The browser's
// timezone is never applied, so there is no +5:30 shift.
// ---------------------------------------------------------------------------
function toWallMs(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  // Handles Mongo extended JSON ({ $date: "..." }) just in case
  if (typeof value === "object" && value.$date) return toWallMs(value.$date);
  let s = String(value).trim().replace(" ", "T");
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) s += "Z";
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : ms;
}

const fmtDate = (ms) =>
  ms
    ? new Date(ms).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })
    : "—";

const fmtTime = (ms) =>
  ms
    ? new Date(ms).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "UTC",
      })
    : "—";

// Duration (in milliseconds) -> "3 min 56 sec", "45 sec", "1 hr 05 min"
const fmtDuration = (ms) => {
  if (ms === null || ms === undefined) return "In progress";
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec} sec`;
  const totalMin = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (totalMin < 60) return sec ? `${totalMin} min ${String(sec).padStart(2, "0")} sec` : `${totalMin} min`;
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return `${hr} hr ${String(min).padStart(2, "0")} min`;
};

const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "a" : "p"}`;
const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h % 24 < 12 ? "AM" : "PM"}`;
const hourRange = (h) => `${fmtHour(h)} – ${fmtHour((h + 1) % 24)}`;

// Today's date as yyyy-mm-dd (from the user's local calendar day)
const todayString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function normalize(r) {
  const startMs = toWallMs(pick(r, FIELDS.start));
  const endMs = toWallMs(pick(r, FIELDS.end));
  const durSecRaw = pick(r, FIELDS.durationSec);
  const durSec = durSecRaw === null ? NaN : Number(durSecRaw);
  const absRaw = pick(r, FIELDS.maxAbsenceSec);
  const absSec = absRaw === null ? NaN : Number(absRaw);

  // Prefer the reported duration (seconds); fall back to end - start
  let durationMs = null;
  if (!Number.isNaN(durSec) && durSec >= 0) durationMs = durSec * 1000;
  else if (startMs !== null && endMs !== null) durationMs = Math.max(0, endMs - startMs);

  const explicit = String(pick(r, FIELDS.status) || "").toLowerCase();
  const ongoing =
    explicit === "in progress" ||
    explicit === "in_progress" ||
    explicit === "ongoing" ||
    (explicit !== "completed" && endMs === null && durationMs === null);

  return {
    id: String(pick(r, FIELDS.id) ?? `${startMs}-${Math.random()}`),
    store: pick(r, FIELDS.store) || "—",
    startMs,
    endMs,
    durationMs,
    maxAbsenceMs: !Number.isNaN(absSec) ? absSec * 1000 : null,
    ongoing,
    count: pick(r, FIELDS.count),
    startImage: pick(r, FIELDS.startImage),
    endImage: pick(r, FIELDS.endImage),
    raw: r,
  };
}

// Builds the list of images available for a session (used by the image modal)
const sessionImages = (r) =>
  [
    r.startImage ? { label: "Start", src: r.startImage } : null,
    r.endImage ? { label: "End", src: r.endImage } : null,
  ].filter(Boolean);

// Compact page list: 1 … 4 5 6 … 20
const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current - 1, current, current + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("gap");
    out.push(n);
  });
  return out;
};

// ---------------------------------------------------------------------------
// Small UI pieces
// ---------------------------------------------------------------------------
function getBatchType(session) {
  const elapsedMs =
    session.durationMs !== null && session.durationMs !== undefined
      ? session.durationMs
      : session.ongoing && session.startMs !== null
        ? Math.max(0, Date.now() - session.startMs)
        : null;
  if (elapsedMs === null) return "—";
  return elapsedMs > 8 * 60 * 1000 ? "Continuous" : "Single";
}

function StatusPill({ ongoing }) {
  return ongoing ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
      Frying
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Completed
    </span>
  );
}

function StatCard({ icon, tint, value, label, hint }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl ${tint}`}>{icon}</span>
      <div className="min-w-0">
        <p className="truncate text-2xl font-semibold text-slate-800">{value}</p>
        <p className="truncate text-sm text-slate-500">{label}</p>
        {hint && <p className="truncate text-xs text-slate-400">{hint}</p>}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-12 rounded-xl bg-slate-100" />
      ))}
    </div>
  );
}

// Small clickable image thumbnail. Falls back to a placeholder if missing / broken.
function Thumb({ src, alt, onOpen, size = "h-10 w-14" }) {
  const [failed, setFailed] = useState(false);

  // If the src changes (e.g. new page of rows) give the image another chance
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <span
        className={`inline-flex ${size} items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-300`}
        title={src ? "Image could not be loaded" : "No image"}
      >
        <PictureOutlined />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      title="Click to view"
      className={`group relative block ${size} overflow-hidden rounded-lg border border-slate-200 bg-slate-100 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-110"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 text-white opacity-0 transition group-hover:bg-slate-900/30 group-hover:opacity-100">
        <ExpandAltOutlined />
      </span>
    </button>
  );
}

// Full-size image viewer (lightbox)
function ImageModal({ preview, onChange, onClose }) {
  const [failed, setFailed] = useState(false);
  const current = preview.items[preview.index];

  useEffect(() => setFailed(false), [current?.src]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
          <div>
            <h3 className="text-base font-semibold text-slate-800">{preview.title}</h3>
            <p className="text-xs text-slate-400">{preview.subtitle}</p>
          </div>

          <div className="flex items-center gap-2">
            {preview.items.length > 1 && (
              <div className="inline-flex rounded-xl bg-slate-100 p-1 text-xs font-medium">
                {preview.items.map((it, i) => (
                  <button
                    key={it.label}
                    onClick={() => onChange(i)}
                    className={`rounded-lg px-3 py-1.5 transition ${
                      i === preview.index ? "bg-white text-orange-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <div className="flex min-h-[200px] flex-1 items-center justify-center overflow-auto bg-slate-950 p-2">
          {failed ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <PictureOutlined className="text-3xl" />
              <p className="text-sm">Image could not be loaded</p>
            </div>
          ) : (
            <img
              src={current.src}
              alt={`${current.label} frame`}
              onError={() => setFailed(true)}
              className="max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
            />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
          <span>{current.label} frame</span>
          <a
            href={current.src}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-orange-600 hover:text-orange-700"
          >
            Open ↗
          </a>
        </div>
      </div>
    </div>
  );
}

function HourTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400">{d.range}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800">
        {d.sessions} {d.sessions === 1 ? "session" : "sessions"}
      </p>
      <p className="text-xs text-slate-500">{d.percent}% of all sessions</p>
    </div>
  );
}

// X-axis tick: shows the full hour ("11 AM", "12 PM") so every bar is labelled.
// Compact mode stacks the number over AM/PM so all bars fit in a narrow card.
function HourTick({ x, y, payload, compact }) {
  const h = payload.value;
  const num = h % 12 === 0 ? 12 : h % 12;
  const ap = h % 24 < 12 ? "AM" : "PM";
  if (compact) {
    return (
      <text x={x} y={y} textAnchor="middle" fill="#475569">
        <tspan x={x} dy={14} fontSize={11} fontWeight={500}>
          {num}
        </tspan>
        <tspan x={x} dy={12} fontSize={9} fill="#94A3B8">
          {ap}
        </tspan>
      </text>
    );
  }
  return (
    <text x={x} y={y} dy={16} textAnchor="middle" fontSize={12} fill="#475569">
      {num} {ap}
    </text>
  );
}

function SessionsByHourChart({ data, peak, total, expanded = false, height = 240 }) {
  const [active, setActive] = useState(null);

  if (!data.length) {
    return (
      <div className="flex h-56 items-center justify-center p-4 text-sm text-slate-400">No data for this date</div>
    );
  }

  const PEAK = "#EA580C";
  const NORMAL = "#FDBA74";
  const HOVER = "#FB923C";

  return (
    <div className="p-4">
      {/* Summary */}
      <div className={`mb-4 grid gap-3 ${expanded ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
        <div className="rounded-xl bg-orange-50 p-3">
          <p className="text-xs text-orange-600/80">Busiest hour</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{peak.range}</p>
          <p className="text-xs text-slate-500">
            {peak.sessions} {peak.sessions === 1 ? "session" : "sessions"}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Total sessions</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">{total}</p>
          <p className="text-xs text-slate-500">
            {fmtHour(data[0].hour)} – {fmtHour((data[data.length - 1].hour + 1) % 24)}
          </p>
        </div>
        {expanded && (
          <div className="rounded-xl bg-sky-50 p-3">
            <p className="text-xs text-sky-700/80">Share in busiest hour</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-800">{peak.percent}%</p>
            <p className="text-xs text-slate-500">of all sessions</p>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 8, left: expanded ? -12 : -28, bottom: 0 }}
            barCategoryGap={expanded ? "22%" : "14%"}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis
              dataKey="hour"
              tickFormatter={(value) => {
                const hour = Number(value);

                if (Number.isNaN(hour)) return value;

                const period = hour >= 12 ? "PM" : "AM";
                const displayHour = hour % 12 || 12;

                return `${displayHour} ${period}`;
              }}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={5}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#94A3B8" }}
              axisLine={false}
              tickLine={false}
              domain={[0, (max) => Math.max(4, Math.ceil(max * 1.1))]}
            />
            <Tooltip content={<HourTooltip />} cursor={{ fill: "#FFF7ED" }} offset={16} />
            <Bar
              dataKey="sessions"
              name="Sessions"
              radius={[6, 6, 0, 0]}
              maxBarSize={expanded ? 44 : 22}
              onMouseEnter={(_, i) => setActive(i)}
              onMouseLeave={() => setActive(null)}
            >
              {data.map((d, i) => (
                <Cell key={d.hour} fill={active === i ? HOVER : d.hour === peak.hour ? PEAK : NORMAL} />
              ))}
              <LabelList
                dataKey="sessions"
                position="top"
                formatter={(v) => (v > 0 ? v : "")}
                style={{ fontSize: expanded ? 12 : 10, fill: "#475569", fontWeight: 500 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend + hint */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PEAK }} /> Busiest hour
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: NORMAL }} /> Other hours
          </span>
        </div>
        <span className="text-slate-400">Hour when the frying started</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function KababMonitoring() {
  const [storeName, setStoreName] = useState(localStorage.getItem(STORE_STORAGE_KEY) || "All Stores");

  // Server data
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [hourlyRaw, setHourlyRaw] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);

  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");

  // Selected day (yyyy-mm-dd). Defaults to today and is sent to the backend,
  // which compares it with postgresCreatedAt.
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [storeFilter, setStoreFilter] = useState("All Stores");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);

  // Image viewer: { title, subtitle, items: [{label, src}], index }
  const [preview, setPreview] = useState(null);

  // Ignores responses from outdated requests
  const requestRef = useRef(0);
  // Last filter combination whose KPIs / chart / dropdown we already loaded
  const statsKeyRef = useRef(null);

  // Everything that changes the KPIs, chart and dropdown (not the page)
  const filterKey = `${storeName}|${selectedDate}|${storeFilter}|${debouncedQuery.trim()}`;

  // Page is tied to the filters: any filter change automatically means page 1,
  // without triggering an extra request.
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;
  const goToPage = (p) => setPageState({ key: filterKey, page: p });

  const openPreview = useCallback((r, which) => {
    const items = sessionImages(r);
    if (!items.length) return;
    const index = Math.max(0, items.findIndex((it) => it.label === which));
    setPreview({
      title: "Frying session",
      subtitle: `${r.store} · ${fmtDate(r.startMs)}, ${fmtTime(r.startMs)}`,
      items,
      index,
    });
  }, []);

  const isToday = selectedDate === todayString();
  const dateLabel = isToday ? "Today" : fmtDate(Date.parse(`${selectedDate}T00:00:00Z`));

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Keep in sync if the store is switched from another tab / header dropdown
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORE_STORAGE_KEY) {
        setStoreName(e.newValue || "All Stores");
        setStoreFilter("All Stores");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Close any open modal with the Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPreview(null);
        setChartOpen(false);
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fetchData = useCallback(
    async (force = false) => {
      const reqId = ++requestRef.current;

      // When only the page changed, skip the KPI / chart / dropdown work
      const needStats = force || statsKeyRef.current !== filterKey;

      setFetching(true);
      setError("");
      try {
        const res = await axios.post("/get-empire-kabab-fry", {
          store: storeName,
          date: selectedDate,
          page,
          limit: PAGE_SIZE,
          restaurant: storeName === "All Stores" && storeFilter !== "All Stores" ? storeFilter : "ALL",
          search: debouncedQuery.trim(),
          includeStats: needStats,
        });

        if (reqId !== requestRef.current) return; // a newer request replaced this one

        const p = res?.data || {};
        setRows((p.data || []).map(normalize));
        setPagination(p.pagination || EMPTY_PAGINATION);

        if (needStats && p.stats) {
          setStats({ ...EMPTY_STATS, ...p.stats });
          setHourlyRaw(p.hourly || []);
          setStoreOptions(p.stores || []);
          statsKeyRef.current = filterKey;
        }
      } catch (err) {
        if (reqId !== requestRef.current) return;
        setError(err?.response?.data?.message || err.message || "Could not load kabab frying data.");
        setRows([]);
        setPagination(EMPTY_PAGINATION);
      } finally {
        if (reqId === requestRef.current) setFetching(false);
      }
    },
    [storeName, selectedDate, storeFilter, debouncedQuery, page, filterKey]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Date change: go to that day and reset the store dropdown
  // (its options are different for each day)
  const changeDate = (value) => {
    setSelectedDate(value || todayString());
    setStoreFilter("All Stores");
  };

  const storeList = useMemo(() => ["All Stores", ...storeOptions], [storeOptions]);

  // Sessions by hour of day (hour taken straight from the start time)
  const hourData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: hourLabel(h),
      range: hourRange(h),
      sessions: 0,
      percent: 0,
    }));
    hourlyRaw.forEach((h) => {
      if (h.hour >= 0 && h.hour < 24) buckets[h.hour].sessions = h.sessions;
    });
    const total = buckets.reduce((s, b) => s + b.sessions, 0);
    buckets.forEach((b) => {
      b.percent = total ? Math.round((b.sessions / total) * 100) : 0;
    });
    const used = buckets.filter((b) => b.sessions > 0).map((b) => b.hour);
    if (!used.length) return [];
    const lo = Math.max(0, Math.min(...used) - 1);
    const hi = Math.min(23, Math.max(...used) + 1);
    return buckets.slice(lo, hi + 1);
  }, [hourlyRaw]);

  const hourTotal = useMemo(() => hourData.reduce((s, b) => s + b.sessions, 0), [hourData]);
  const peakHour = useMemo(
    () => (hourData.length ? hourData.reduce((best, b) => (b.sessions > best.sessions ? b : best), hourData[0]) : null),
    [hourData]
  );

  // Pagination numbers (from the server)
  const totalRows = pagination.total;
  const totalPages = Math.max(1, pagination.totalPages);
  const currentPage = pagination.page;
  const startIdx = totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(currentPage * PAGE_SIZE, totalRows);
  const pageList = buildPageList(currentPage, totalPages);

  const showSkeleton = fetching && rows.length === 0;
  const showEmpty = !fetching && rows.length === 0;

  const selectClass =
    "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-100";

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-orange-50/60 via-slate-50 to-slate-50 text-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 text-xl text-orange-600">
              <FireOutlined />
            </span>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Kebab Frying Monitor</h1>
              <p className="text-sm text-slate-500">
                Every frying session captured at the kabab station, with start, end and duration.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm font-medium text-orange-700">
              <ShopOutlined />
              {storeName}
            </span>

            {/* Date filter (defaults to today, sent to the backend) */}
            <input
              type="date"
              value={selectedDate}
              max={todayString()}
              onChange={(e) => changeDate(e.target.value)}
              aria-label="Select date"
              className="rounded-xl border border-orange-300 bg-white px-3 py-2 text-sm text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-100"
            />

            <button
              onClick={() => fetchData(true)}
              disabled={fetching}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              <ReloadOutlined spin={fetching} />
              Refresh
            </button>
          </div>
        </header>

        {/* KPI cards */}
        <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<FireOutlined />}
            tint="bg-orange-50 text-orange-600"
            value={stats.total}
            label="Frying sessions"
            hint={dateLabel}
          />
          <StatCard
            icon={<ThunderboltOutlined />}
            tint="bg-amber-50 text-amber-600"
            value={stats.active}
            label="Frying right now"
            hint="Sessions still in progress"
          />
          <StatCard
            icon={<ClockCircleOutlined />}
            tint="bg-sky-50 text-sky-600"
            value={stats.avgSeconds === null ? "—" : fmtDuration(stats.avgSeconds * 1000)}
            label="Average frying time"
          />
          <StatCard
            icon={<CalendarOutlined />}
            tint="bg-emerald-50 text-emerald-600"
            value={stats.longestSeconds === null ? "—" : fmtDuration(stats.longestSeconds * 1000)}
            label="Longest session"
            hint={storeName === "All Stores" ? `${stats.storeCount} stores` : undefined}
          />
        </section>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <ExclamationCircleOutlined className="mt-0.5 text-base" />
            <div className="flex-1">
              <p className="font-medium">Couldn’t load frying data</p>
              <p className="text-rose-600">{error}</p>
            </div>
            <button
              onClick={() => fetchData(true)}
              className="rounded-lg bg-white px-3 py-1.5 font-medium text-rose-700 ring-1 ring-rose-100 hover:bg-rose-50"
            >
              Try again
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Sessions list */}
          <section className="rounded-2xl border border-slate-100 bg-white shadow-sm xl:col-span-2">
            <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Frying sessions</h2>
                <p className="text-xs text-slate-400">
                  {totalRows.toLocaleString()} sessions · {dateLabel}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative">
                  <SearchOutlined className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search store"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm placeholder-slate-400 focus:border-orange-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100 sm:w-56"
                  />
                </div>
                {storeName === "All Stores" && storeList.length > 2 && (
                  <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className={selectClass}>
                    {storeList.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {showSkeleton ? (
              <Skeleton />
            ) : showEmpty ? (
              <div className="flex flex-col items-center gap-2 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-2xl text-slate-300">
                  <InboxOutlined />
                </span>
                <p className="font-medium text-slate-600">No frying sessions found</p>
                <p className="max-w-xs text-sm text-slate-400">
                  Try a different date or clear the search and store filters.
                </p>
                {!isToday && (
                  <button
                    onClick={() => changeDate(todayString())}
                    className="mt-2 rounded-xl bg-orange-100 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-200"
                  >
                    Show today
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Rows dim slightly while the next page is loading */}
                <div className={`transition-opacity ${fetching ? "opacity-60" : "opacity-100"}`}>
                  {/* Desktop table */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/60 text-xs text-slate-400">
                          <th className="px-4 py-3 font-medium">Started</th>
                          <th className="px-4 py-3 font-medium">Start image</th>
                          <th className="px-4 py-3 font-medium">Ended</th>
                          <th className="px-4 py-3 font-medium">End image</th>
                          <th className="px-4 py-3 font-medium">Duration</th>
                          <th className="px-4 py-3 font-medium">Batch Type</th>
                          <th className="px-4 py-3 font-medium">Store</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id} className="border-b border-slate-50 last:border-0 hover:bg-orange-50/30">
                            <td className="whitespace-nowrap px-4 py-3">
                              <p className="font-medium text-slate-700">{fmtTime(r.startMs)}</p>
                              <p className="text-xs text-slate-400">{fmtDate(r.startMs)}</p>
                            </td>
                            <td className="px-4 py-3">
                              <Thumb src={r.startImage} alt="Start frame" onOpen={() => openPreview(r, "Start")} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                              {r.endMs ? fmtTime(r.endMs) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <Thumb src={r.endImage} alt="End frame" onOpen={() => openPreview(r, "End")} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-700">
                              {fmtDuration(r.durationMs)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                  getBatchType(r) === "Continuous"
                                    ? "bg-orange-50 text-orange-700"
                                    : getBatchType(r) === "Single"
                                      ? "bg-sky-50 text-sky-700"
                                      : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {getBatchType(r)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">{r.store}</td>
                            <td className="px-4 py-3">
                              <StatusPill ongoing={r.ongoing} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <ul className="divide-y divide-slate-50 md:hidden">
                    {rows.map((r) => (
                      <li key={r.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-800">
                              {fmtTime(r.startMs)}{" "}
                              <span className="font-normal text-slate-400">· {fmtDate(r.startMs)}</span>
                            </p>
                            <p className="mt-0.5 text-sm text-slate-500">{r.store}</p>
                          </div>
                          <StatusPill ongoing={r.ongoing} />
                        </div>

                        {/* Thumbnails */}
                        <div className="mt-3 flex items-center gap-3">
                          <div>
                            <p className="mb-1 text-[11px] text-slate-400">Start</p>
                            <Thumb
                              src={r.startImage}
                              alt="Start frame"
                              size="h-14 w-20"
                              onOpen={() => openPreview(r, "Start")}
                            />
                          </div>
                          <div>
                            <p className="mb-1 text-[11px] text-slate-400">End</p>
                            <Thumb
                              src={r.endImage}
                              alt="End frame"
                              size="h-14 w-20"
                              onOpen={() => openPreview(r, "End")}
                            />
                          </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
                            <ClockCircleOutlined /> {fmtDuration(r.durationMs)}
                          </span>
                          <button
                            onClick={() => setSelected(r)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"
                          >
                            <EyeOutlined /> View
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pagination */}
                <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500 sm:flex-row">
                  <span>
                    {totalRows === 0
                      ? "0 sessions"
                      : `Showing ${startIdx}-${endIdx} of ${totalRows.toLocaleString()} sessions`}
                  </span>
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    <button
                      disabled={currentPage <= 1 || fetching}
                      onClick={() => goToPage(currentPage - 1)}
                      aria-label="Previous page"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <LeftOutlined /> Prev
                    </button>

                    {pageList.map((p, i) =>
                      p === "gap" ? (
                        <span key={`gap-${i}`} className="px-1 text-slate-400">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => goToPage(p)}
                          disabled={fetching}
                          aria-label={`Page ${p}`}
                          aria-current={p === currentPage ? "page" : undefined}
                          className={`min-w-[2rem] rounded-lg border px-2 py-1.5 tabular-nums ${
                            p === currentPage
                              ? "border-orange-300 bg-orange-50 font-medium text-orange-700"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}

                    <button
                      disabled={currentPage >= totalPages || fetching}
                      onClick={() => goToPage(currentPage + 1)}
                      aria-label="Next page"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40"
                    >
                      Next <RightOutlined />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Side column */}
          <aside className="flex flex-col gap-6">
            <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 border-b border-slate-100 p-4">
                <div className="flex items-center gap-2">
                  <BarChartOutlined className="text-orange-500" />
                  <h2 className="text-base font-semibold text-slate-800">Sessions by Hour</h2>
                </div>
                <button
                  onClick={() => setChartOpen(true)}
                  disabled={hourData.length === 0}
                  aria-label="Expand chart"
                  title="Expand chart"
                  className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-40"
                >
                  <ExpandAltOutlined />
                </button>
              </div>
              <SessionsByHourChart data={hourData} peak={peakHour} total={hourTotal} />
            </section>
          </aside>
        </div>
      </div>

      {/* Expanded chart modal */}
      {chartOpen && peakHour && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/30 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setChartOpen(false)}
        >
          <div
            className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-t-3xl bg-white shadow-xl sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <div className="flex items-center gap-2">
                <BarChartOutlined className="text-orange-500" />
                <div>
                  <h3 className="text-base font-semibold text-slate-800">Sessions by hour</h3>
                  <p className="text-xs text-slate-400">
                    {storeName} · {dateLabel}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setChartOpen(false)}
                aria-label="Close"
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
              >
                <CloseOutlined />
              </button>
            </div>
            <SessionsByHourChart data={hourData} peak={peakHour} total={hourTotal} expanded height={420} />
          </div>
        </div>
      )}

      {/* Image viewer (always on top of other modals) */}
      {preview && (
        <ImageModal
          preview={preview}
          onChange={(index) => setPreview((p) => ({ ...p, index }))}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}